import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TabDirective, TabsModule } from 'ngx-bootstrap/tabs';
import { MatchTable } from '../../models/matchs/match-table.model';
import { MatchsResultsResponse } from '../../models/matchs/responses/api-matchs-results-response.model';
import { ApiMatchsService } from '../../services/matchs/api/api-matchs.service';
import { AlertsService } from '../../services/alerts.service';
import { ModalsService } from '../../services/modals.service';
import { MatchTableAddModalComponent } from './modals/match-table-add-modal/match-table-add-modal.component';
import { MatchTableEditModalComponent } from './modals/match-table-edit-modal/match-table-edit-modal.component';
import { MatchsStatisticsTabComponent } from './tabs/matchs-statistics-tab/matchs-statistics-tab.component';

@Component({
  templateUrl: 'matchs.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, TooltipModule, TabDirective, TabsModule, MatchTableAddModalComponent, MatchTableEditModalComponent, MatchsStatisticsTabComponent]
})
export class MatchsComponent implements OnInit {

  @ViewChild('addModal') addModal!: MatchTableAddModalComponent;
  @ViewChild('editModal') editModal!: MatchTableEditModalComponent;
  @ViewChild('statisticsTab') statisticsTab!: MatchsStatisticsTabComponent;

  onSelectStatisticsTab(): void { this.statisticsTab.load(); }

  isLoading: boolean = false;

  matchTables: MatchTable[] = [];
  resultsPerPage: number = 25;
  totalResults: number = 0;

  private static pageNumber: number = 1;

  get pageNumber() {
    return MatchsComponent.pageNumber;
  }

  get totalPages(): number {
    return Math.ceil(this.totalResults / this.resultsPerPage);
  }

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pageNumber;

    const visibleSet = new Set<number>();

    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);

    const sorted = Array.from(visibleSet).sort((a, b) => a - b);

    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }

    return result;
  }

  goToPage(page: number): void {
    MatchsComponent.pageNumber = page;
    this.refreshMatchTablesList();
  }

  constructor(
    private apiMatchsService: ApiMatchsService,
    private alertsService: AlertsService,
    private modalsService: ModalsService
  ) {}

  ngOnInit(): void {
    this.refreshMatchTablesList();
  }

  onClickPaginationPrevious(): void {
    MatchsComponent.pageNumber--;
    this.refreshMatchTablesList();
  }

  onClickPaginationNext(): void {
    MatchsComponent.pageNumber++;
    this.refreshMatchTablesList();
  }

  onClickAdd(): void {
    this.addModal.show(() => this.refreshMatchTablesList());
  }

  onClickEdit(matchTable: MatchTable): void {
    this.editModal.show(
      matchTable.matchTableId,
      matchTable.name,
      matchTable.levelMinimumRequired,
      matchTable.levelExperienceRewardQuantity,
      matchTable.leaguePointRewardQuantity,
      matchTable.leaguePointRewardQuantity2,
      matchTable.hammerRewardQuantity,
      matchTable.creditRewardQuantity1,
      matchTable.creditRewardQuantity2,
      matchTable.creditRewardQuantityDraw,
      matchTable.matchCost,
      matchTable.matchRank,
      matchTable.variantId,
      () => this.refreshMatchTablesList()
    );
  }

  onClickDelete(matchTable: MatchTable): void {
    this.modalsService.showConfirmModal(() => {
      this.apiMatchsService.deleteMatchTable(matchTable.matchTableId).then(
        () => {
          this.alertsService.displayAlertSuccess('Match table deleted !');
          this.refreshMatchTablesList();
        }, error => {
          this.alertsService.displayAlertError('Error deleting match table : ' + error);
        }
      );
    });
  }

  private refreshMatchTablesList(): void {
    this.isLoading = true;

    this.apiMatchsService.getMatchsTables(MatchsComponent.pageNumber, this.resultsPerPage).then(
      (response: MatchsResultsResponse) => {
        this.isLoading = false;
        this.matchTables = response.results;
        this.totalResults = response.resultsTotal;
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading match tables : ' + error);
      }
    );
  }
}
